// Parses the filename out of a `Content-Disposition: attachment; filename="..."`
// header. Prefers the RFC 5987 `filename*=UTF-8''...` extended form when present,
// otherwise falls back to the quoted or bare `filename=...` form. Returns null
// when the header is absent, has no filename parameter, or fails to decode.
//
// Backend contract is UTF-8 only; the charset segment of `filename*=` is ignored.
// Output is normalized to strip control chars and BIDI override (which can spoof
// extensions in the OS save dialog) and capped at 255 chars.
const FILENAME_MAX_LENGTH = 255;
// Strips ASCII control chars and BIDI override codepoints from the server-supplied
// filename before it reaches `<a download>` to prevent extension-spoof rendering.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\x00-\x1F\x7F\u202A-\u202E\u2066-\u2069]/g;

function normalizeFilename(raw: string): string | null {
  const cleaned = raw.replace(UNSAFE_CHARS, '').slice(0, FILENAME_MAX_LENGTH);
  return cleaned.length > 0 ? cleaned : null;
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const extendedMatch = header.match(/filename\*\s*=\s*(?:[^']*)'(?:[^']*)'([^;]+)/i);
  if (extendedMatch) {
    const encoded = extendedMatch[1].trim();
    try {
      return normalizeFilename(decodeURIComponent(encoded));
    } catch {
      return null;
    }
  }

  const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch) {
    return normalizeFilename(quotedMatch[1]);
  }

  const bareMatch = header.match(/filename\s*=\s*([^;]+)/i);
  if (bareMatch) {
    return normalizeFilename(bareMatch[1].trim());
  }

  return null;
}

export default parseContentDispositionFilename;
